import { useState } from 'react';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { useQueryClient } from '@tanstack/react-query';
import { IPriceRule } from '../../types/price.types';
import { ChannelRuleSteps } from '../ChannelRuleModal/ChannelRuleModalContent';
import { deletePriceRule } from 'src/api/deletePriceRule/deletePriceRule';
import { PRICE_RULES_QUERY } from 'src/hooks/QueryKeys';

type TEditChannelRule = {
  channel: string | null;
  // Channel the new rule is being created for; the modal skips its channel picker when set.
  newRuleChannelId: string | null;
  step: number | null;
};

const DEFAULT_STATE: TEditChannelRule = { channel: null, newRuleChannelId: null, step: null };

export const useChannelRuleActions = (priceRules: IPriceRule[], priceSchemeId: string) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [editingChannelRule, setEditingChannelRule] = useState<TEditChannelRule>(DEFAULT_STATE);
  const [deletingChannelRule, setDeletingChannelRule] = useState<string | null>(null);

  const channelRules = priceRules.filter((rule) => rule.extension_instance);
  const activeChannel = channelRules.find((rule) => rule.price_rule_id === editingChannelRule.channel);

  // Creation always starts from a specific channel row, so the picker step is redundant.
  const handleCreateClick = (extensionInstanceId: string) =>
    setEditingChannelRule({ channel: null, newRuleChannelId: extensionInstanceId, step: ChannelRuleSteps.Adjustment });
  const handleChannelEditClick = (priceRuleId: string, step: ChannelRuleSteps) =>
    setEditingChannelRule({ channel: priceRuleId, newRuleChannelId: null, step });
  const handleDeleteClick = (priceRuleId: string) => setDeletingChannelRule(priceRuleId);
  const resetEditingChannelRule = () => setEditingChannelRule(DEFAULT_STATE);

  const handleDelete = async (ruleId: string) => {
    try {
      await deletePriceRule(ruleId, priceSchemeId);
      await queryClient.invalidateQueries({ queryKey: PRICE_RULES_QUERY });
      showSnackbar({ status: SnackbarStatus.SUCCESS, message: 'Price rule deleted successfully' });
    } catch {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Failed to delete price rule' });
    } finally {
      setDeletingChannelRule(null);
    }
  };

  return {
    editingChannelRule,
    deletingChannelRule,
    setDeletingChannelRule,
    activeChannel,
    handleCreateClick,
    handleChannelEditClick,
    handleDeleteClick,
    resetEditingChannelRule,
    handleDelete,
  };
};
